// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Box, Button, Checkbox, Group, Modal, Select, Stack, Text, Textarea } from '@mantine/core';
import { useForm } from '@mantine/form';
import type { Appointment, Encounter } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import type { JSX } from 'react';
import { useState } from 'react';
import { recordMissedVisitFee } from '../../services/appointment-missed-visit-fee';
import { updateEncounterStatus } from '../../utils/encounter';
import { showErrorNotification } from '../../utils/notifications';

const BRAND = '#640040';
const CONTENT_BG = 'var(--mantine-color-gray-0)';

const OUTCOME_OPTIONS = [
  { value: 'completed', label: 'Completed' },
  { value: 'no-show', label: 'Patient No-Show' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'rescheduled', label: 'Rescheduled' },
  { value: 'technical-issues', label: 'Technical Issues' },
];
const EXCEPTION_OPTIONS = [
  { value: 'emergency', label: 'Documented emergency' },
  { value: 'hospitalization', label: 'Hospitalization' },
  { value: 'technical-failure', label: 'Polaris technical failure' },
];

interface ConcludeSessionModalProps {
  opened: boolean;
  onClose: () => void;
  encounter: Encounter;
  appointment: Appointment | undefined;
  /** Receives the concluded Encounter so callers holding local state can refresh in place. */
  onConcluded: (encounter: Encounter) => void;
  onBeforeConclude?: () => Promise<void>;
}

export function ConcludeSessionModal({
  opened,
  onClose,
  encounter,
  appointment,
  onConcluded,
  onBeforeConclude,
}: ConcludeSessionModalProps): JSX.Element {
  const medplum = useMedplum();
  const [loading, setLoading] = useState(false);

  const form = useForm({
    initialValues: { outcome: '', checklistConfirmed: false, notes: '', exception: '', waiveFee: false },
    // Without this, submitting with no outcome fell through to the cancel branch
    // and silently marked the appointment cancelled — losing it from both the
    // Today's and Recent queues with nothing left owing a note.
    validate: {
      outcome: (value) => (value ? null : 'Select an appointment outcome before concluding'),
    },
  });

  const handleConclude = form.onSubmit(async (values) => {
    setLoading(true);
    try {
      await onBeforeConclude?.();
      // Read the encounter from Medplum after the save so updateEncounterStatus spreads
      // the post-save data (reasonCode, diagnosis, etc.) rather than the stale prop.
      // medplum.readResource returns from the internal cache which was updated by saveToFhir's updateResource call.
      const currentEncounter = encounter.id ? await medplum.readResource('Encounter', encounter.id) : encounter;
      let concluded: Encounter;
      switch (values.outcome) {
        case 'completed':
          concluded = await updateEncounterStatus(medplum, currentEncounter, appointment, 'finished');
          break;
        case 'no-show':
          concluded = await updateEncounterStatus(medplum, currentEncounter, appointment, 'cancelled', {
            noshow: true,
          });
          if (appointment) {
            await recordMissedVisitFee(medplum, appointment, 'no-show', {
              waived: values.waiveFee,
              exception: values.exception as 'emergency' | 'hospitalization' | 'technical-failure' | undefined,
            });
          }
          break;
        case 'cancelled':
          concluded = await updateEncounterStatus(medplum, currentEncounter, appointment, 'cancelled');
          if (appointment) {
            await recordMissedVisitFee(medplum, appointment, 'cancelled', {
              waived: values.waiveFee,
              exception: values.exception as 'emergency' | 'hospitalization' | 'technical-failure' | undefined,
            });
          }
          break;
        case 'rescheduled':
        case 'technical-issues':
          concluded = await updateEncounterStatus(medplum, currentEncounter, appointment, 'cancelled', {
            outcome: values.outcome,
          });
          break;
        default:
          // Unreachable while the form validates, and deliberately not a fall-through
          // to cancelling: an unrecognised outcome must surface as an error rather
          // than quietly discard the appointment.
          throw new Error(`Unrecognized appointment outcome: "${values.outcome}"`);
      }
      form.reset();
      onConcluded(concluded);
    } catch (err) {
      showErrorNotification(err);
    } finally {
      setLoading(false);
    }
  });

  return (
    <Modal opened={opened} onClose={onClose} withCloseButton={false} size="lg" padding={0} radius="md">
      <Stack gap={24} align="center" style={{ paddingTop: 38, paddingBottom: 24 }}>
        <Text fw={700} fz={32} lh="40px" ta="center">
          Conclude This Session?
        </Text>

        <Box
          style={{
            width: 'calc(100% - 48px)',
            backgroundColor: CONTENT_BG,
            borderRadius: 8,
            paddingTop: 29,
            paddingBottom: 29,
            paddingLeft: 24,
            paddingRight: 24,
          }}
        >
          <Stack gap={20} align="center">
            <Text fz={16} lh="24px" style={{ maxWidth: 420, width: '100%' }}>
              Concluding the session will end the video call. Please ensure that you have let the patient know that the
              session is over and that you have satisfied the requirements of the appointment checklist before
              concluding the session.
            </Text>

            <Select
              label="Appointment Outcome"
              placeholder="Select an outcome"
              data={OUTCOME_OPTIONS}
              style={{ maxWidth: 420, width: '100%' }}
              {...form.getInputProps('outcome')}
            />

            <Checkbox
              label="I have satisfied the requirements of the appointment checklist"
              style={{ maxWidth: 420, width: '100%' }}
              styles={{ label: { fontWeight: 500 } }}
              {...form.getInputProps('checklistConfirmed', { type: 'checkbox' })}
            />

            <Textarea
              label="Notes"
              placeholder="Any additional notes about this session..."
              rows={4}
              style={{ maxWidth: 420, width: '100%' }}
              {...form.getInputProps('notes')}
            />
            {(form.values.outcome === 'no-show' || form.values.outcome === 'cancelled') && (
              <>
                <Select
                  label="Exception (if applicable)"
                  placeholder="No exception"
                  clearable
                  data={EXCEPTION_OPTIONS}
                  {...form.getInputProps('exception')}
                />
                <Checkbox
                  label="Courtesy-waive missed appointment fee"
                  {...form.getInputProps('waiveFee', { type: 'checkbox' })}
                />
              </>
            )}
          </Stack>
        </Box>

        <Group justify="center" gap={10} style={{ width: 600 }}>
          <Button
            variant="outline"
            size="md"
            style={{ color: BRAND, borderColor: BRAND }}
            disabled={loading}
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button size="md" style={{ backgroundColor: BRAND }} loading={loading} onClick={() => handleConclude()}>
            Conclude Session
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
